// nav/HowToPlayTrigger.jsx: Phase 3 #5b: shared "How to Play" affordance.
//
// A small "?" button that opens the How-to-Play overlay. The open
// callback is supplied by App via OverlayNavContext, so any surface
// (home header, in-room header, in-game header) can drop a trigger
// without prop-drilling the callback through RoomView → RoomLobby →
// NetworkedGameView. Rendering a trigger never touches the live view
// tree, clicking it only flips overlay state in App, which mounts the
// overlay ABOVE RoomView while RoomView (the WS owner) stays mounted.

import { createContext } from 'preact';
import { useContext } from 'preact/hooks';

/**
 * Shared nav callbacks for in-surface affordances. `open` shows the
 * How-to-Play overlay (#5b), supplied by App.
 * @type {import('preact').Context<{ open: () => void } | null>}
 */
export const OverlayNavContext = createContext(/** @type {any} */ (null));

/**
 * The How-to-Play affordance. No-ops (renders nothing) if no provider is
 * present, so it is safe to drop into any surface unconditionally.
 *
 * Two variants:
 *   • default ('icon'), the compact "?" icon button (in-room header).
 *   • 'link' a plainly-labeled "How to play" text link, used below the
 *     lobby CTAs where an icon would be too easy to miss (#4).
 *
 * @param {{ class?: string, variant?: 'icon'|'link' }} props
 */
export function HowToPlayTrigger({ class: className, variant = 'icon' }) {
  const ctx = useContext(OverlayNavContext);
  if (!ctx) return null;
  if (variant === 'link') {
    return (
      <button
        type="button"
        class={`how-to-play-link${className ? ` ${className}` : ''}`}
        data-testid="how-to-play-link"
        onClick={ctx.open}
      >
        How to play
      </button>
    );
  }
  return (
    <button
      type="button"
      class={`how-to-play-trigger${className ? ` ${className}` : ''}`}
      data-testid="how-to-play-trigger"
      onClick={ctx.open}
      aria-label="How to play"
      title="How to play"
    >
      ?
    </button>
  );
}
